import {
  CheckCircleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Modal,
  Row,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import axios from "axios";
import moment from "moment";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FieldBuilder from "../../components/form-components/field-builder";
import PageHeader from "../../components/general/layout/page-header";
import AppContext from "../../context/app/context";
import { ICreateExternalBillingReport } from "../../models/report/request";
import { IReport, ReportStatus } from "../../models/report/response";
import { IStore } from "../../models/store/response";
import EndPoints from "../../services/end-points";
import getApiErrorMessage from "../../utils/api/get-api-error-message";
import { createActiveNamlieahStoreFilters } from "../../utils/helpers/store-filters";
import styles from "./style.module.scss";

const ORDER_NUMBER_PATTERN = /^[0-9]{12}$/;
const POLLING_INTERVAL_MS = 4000;
const ACTIVE_STATUSES = new Set<ReportStatus>(["pending", "processing"]);

interface ExternalBillingFormValues extends ICreateExternalBillingReport {}

const getFilenameFromContentDisposition = (value?: string) => {
  if (!value) return undefined;

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  return value.match(/filename="?([^";]+)"?/i)?.[1];
};

const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename.replace(/[\\/:*?"<>|]/g, "-");
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

const ExternalBillingReportPage = () => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const [stores, setStores] = useState<IStore[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [report, setReport] = useState<IReport | null>(null);
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pollingFailed, setPollingFailed] = useState(false);
  const [action, setAction] = useState<"download" | "confirm" | "retry" | null>(
    null,
  );
  const activeReportIdRef = useRef<number | null>(null);

  const methods = useForm<ExternalBillingFormValues>({
    mode: "onChange",
    defaultValues: {
      storeId: undefined as unknown as number,
      fromOrderNumber: "",
      toOrderNumber: "",
    },
  });
  const { clearErrors, getValues, handleSubmit, reset, setError } = methods;

  const updateReport = useCallback((nextReport: IReport) => {
    activeReportIdRef.current = nextReport.id;
    setReport(nextReport);
  }, []);

  const loadReport = useCallback(
    async (id: number, options?: { background?: boolean }) => {
      if (!options?.background) setRefreshing(true);

      try {
        const response = await EndPoints.report.getReport(id, {
          suppressErrorNotification: options?.background,
        });
        updateReport(response.data);
        setPollingFailed(false);
        return response.data;
      } catch (error) {
        if (options?.background && !axios.isCancel(error)) {
          setPollingFailed(true);
        }
        throw error;
      } finally {
        if (!options?.background) setRefreshing(false);
      }
    },
    [updateReport],
  );

  useEffect(() => {
    let mounted = true;

    const loadStores = async () => {
      try {
        const response = await EndPoints.store.getAllStores({
          light: true,
          perPage: -1,
          filters: createActiveNamlieahStoreFilters(),
        });
        if (mounted) setStores(response.data.data ?? []);
      } catch {
        if (mounted) setStores([]);
      } finally {
        if (mounted) setStoresLoading(false);
      }
    };

    loadStores();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const reportId = report?.id;
    const status = report?.status as ReportStatus | undefined;
    if (!reportId || !status || !ACTIVE_STATUSES.has(status)) return;

    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const poll = async () => {
      try {
        const response = await EndPoints.report.getReport(reportId, {
          signal: controller.signal,
          suppressErrorNotification: true,
        });
        if (!active) return;

        const nextReport = response.data;
        if (activeReportIdRef.current !== reportId) return;
        updateReport(nextReport);
        setPollingFailed(false);

        if (ACTIVE_STATUSES.has(nextReport.status as ReportStatus)) {
          timer = setTimeout(poll, POLLING_INTERVAL_MS);
        }
      } catch (error) {
        if (!active || axios.isCancel(error)) return;
        setPollingFailed(true);
        timer = setTimeout(poll, POLLING_INTERVAL_MS);
      }
    };

    timer = setTimeout(poll, POLLING_INTERVAL_MS);
    return () => {
      active = false;
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [report?.id, report?.status, updateReport]);

  const applyFieldErrors = (error: any) => {
    const errors = error?.response?.data?.errors;
    if (!errors || typeof errors !== "object") return false;

    let applied = false;
    (["storeId", "fromOrderNumber", "toOrderNumber"] as const).forEach(
      (field) => {
        const value = errors[field];
        const fieldMessage = Array.isArray(value) ? value[0] : value;
        if (typeof fieldMessage !== "string" || !fieldMessage) return;
        setError(field, { type: "server", message: fieldMessage });
        applied = true;
      },
    );
    return applied;
  };

  const submit = async (values: ExternalBillingFormValues) => {
    clearErrors();
    setCreating(true);

    try {
      const response = await EndPoints.report.createExternalBillingReport(
        values,
        { suppressErrorNotification: true },
      );
      updateReport(response.data);
      reset({
        storeId: undefined as unknown as number,
        fromOrderNumber: "",
        toOrderNumber: "",
      });
      message.success(t("external_billing_report_queued"));
    } catch (error) {
      if (!applyFieldErrors(error)) {
        message.error(
          getApiErrorMessage(error, t("external_billing_report_create_failed")),
        );
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async () => {
    if (!report?.id || report.canDownload !== true) return;
    setAction("download");

    try {
      const response = await EndPoints.report.downloadReportById(report.id);
      const filename =
        getFilenameFromContentDisposition(
          response.headers?.["content-disposition"],
        ) ?? `external-billing-report-${report.id}.xlsx`;
      saveBlob(
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data]),
        filename,
      );
      message.success(t("external_billing_download_succeeded"));
      await loadReport(report.id, { background: true }).catch(() => undefined);
    } finally {
      setAction(null);
    }
  };

  const handleRetry = async () => {
    if (!report?.id || report.canRetry !== true) return;
    setAction("retry");

    try {
      const response = await EndPoints.report.retryExternalBillingReport(
        report.id,
      );
      updateReport(response.data);
      message.success(t("external_billing_retry_started"));
    } finally {
      setAction(null);
    }
  };

  const handleConfirm = () => {
    if (!report?.id || report.canConfirmBilling !== true) return;

    Modal.confirm({
      rootClassName:
        direction === "rtl" ? "external-billing-rtl-confirm" : undefined,
      title: t("external_billing_confirm_title"),
      content: t("external_billing_confirm_message"),
      okText: t("external_billing_confirm_action"),
      cancelText: t("cancel"),
      okButtonProps: { danger: true },
      async onOk() {
        setAction("confirm");
        try {
          const response = await EndPoints.report.confirmExternalBillingReport(
            report.id,
          );
          updateReport(response.data);
          message.success(t("external_billing_confirmed_successfully"));
        } finally {
          setAction(null);
        }
      },
    });
  };

  const formatDate = (value?: string | null) =>
    value ? moment(value).format("YYYY-MM-DD HH:mm") : t("not_available");

  const status = report?.status ?? "pending";
  const statusColor: Record<string, string> = {
    pending: "gold",
    processing: "processing",
    available: "green",
    confirmed: "blue",
    failed: "red",
    expired: "default",
    cancelled: "default",
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title={t("external_billing_report")}
        subTitle={t("external_billing_report_subtitle")}
        extra={
          report ? (
            <Button
              icon={<ReloadOutlined />}
              loading={refreshing}
              onClick={() => loadReport(report.id).catch(() => undefined)}
            >
              {t("refresh")}
            </Button>
          ) : null
        }
      />

      <Row gutter={[20, 20]} align="top">
        <Col xs={24} xl={9}>
          <Card title={t("external_billing_create_report")}>
            <Typography.Paragraph type="secondary">
              {t("external_billing_form_hint")}
            </Typography.Paragraph>

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(submit)}>
                <Row gutter={[16, 8]}>
                  <Col span={24}>
                    <FieldBuilder
                      name="storeId"
                      label={t("external_billing_namlieah_store")}
                      width="large"
                      rules={{ required: true }}
                      input={{
                        type: "select",
                        allowSearch: true,
                        loading: storesLoading,
                        options: stores.map((store) => ({
                          label: store.name,
                          value: store.id,
                        })),
                      }}
                    />
                  </Col>
                  <Col xs={24} md={12} xl={24}>
                    <FieldBuilder
                      name="fromOrderNumber"
                      label={t("external_billing_from_order_number")}
                      width="large"
                      rules={{
                        required: true,
                        pattern: {
                          value: ORDER_NUMBER_PATTERN,
                          message: t("external_billing_order_number_invalid"),
                        },
                      }}
                      input={{ type: "hidden-type" }}
                    />
                  </Col>
                  <Col xs={24} md={12} xl={24}>
                    <FieldBuilder
                      name="toOrderNumber"
                      label={t("external_billing_to_order_number")}
                      width="large"
                      rules={{
                        required: true,
                        pattern: {
                          value: ORDER_NUMBER_PATTERN,
                          message: t("external_billing_order_number_invalid"),
                        },
                        validate: (value: string) => {
                          const from = getValues("fromOrderNumber");
                          if (
                            ORDER_NUMBER_PATTERN.test(value) &&
                            ORDER_NUMBER_PATTERN.test(from) &&
                            value < from
                          ) {
                            return t("external_billing_range_invalid");
                          }
                          return true;
                        },
                      }}
                      input={{ type: "hidden-type" }}
                    />
                  </Col>
                  <Col span={24}>
                    <Button
                      block
                      size="large"
                      type="primary"
                      htmlType="submit"
                      icon={<FileExcelOutlined />}
                      loading={creating}
                    >
                      {t("external_billing_generate")}
                    </Button>
                  </Col>
                </Row>
              </form>
            </FormProvider>
          </Card>
        </Col>

        <Col xs={24} xl={15}>
          <Card
            className={styles.detailsCard}
            title={t("external_billing_report_details")}
            extra={
              report ? (
                <Tag color={statusColor[status] ?? "default"}>
                  {t(`report_status_${status}`)}
                </Tag>
              ) : null
            }
          >
            {!report ? (
              <div className={styles.emptyState}>
                <FileExcelOutlined aria-hidden />
                <Typography.Title level={4}>
                  {t("external_billing_no_report_yet")}
                </Typography.Title>
                <Typography.Paragraph type="secondary">
                  {t("external_billing_no_report_hint")}
                </Typography.Paragraph>
              </div>
            ) : (
              <Space
                direction="vertical"
                size={16}
                className={styles.fullWidth}
              >
                {ACTIVE_STATUSES.has(status as ReportStatus) ? (
                  <Alert
                    showIcon
                    type="info"
                    message={t("external_billing_generation_in_progress")}
                    description={t("external_billing_polling_hint")}
                  />
                ) : null}

                {pollingFailed ? (
                  <Alert
                    showIcon
                    type="warning"
                    message={t("external_billing_refresh_failed")}
                    action={
                      <Button
                        size="small"
                        onClick={() =>
                          loadReport(report.id).catch(() => undefined)
                        }
                      >
                        {t("retry")}
                      </Button>
                    }
                  />
                ) : null}

                {status === "failed" ? (
                  <Alert
                    showIcon
                    type="error"
                    message={t("report_status_failed")}
                    description={
                      report.failureReason ?? t("report_failed_message")
                    }
                  />
                ) : null}

                <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
                  <Descriptions.Item label={t("external_billing_report_id")}>
                    {report.id}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("status")}>
                    <Tag color={statusColor[status] ?? "default"}>
                      {t(`report_status_${status}`)}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("external_billing_namlieah_store")}
                  >
                    {report.store?.name ?? report.storeId ?? t("not_available")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("external_billing_order_range")}>
                    <span className={styles.orderRange} dir="ltr">
                      {String(report.filters?.fromOrderNumber ?? "-")} →{" "}
                      {String(report.filters?.toOrderNumber ?? "-")}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("external_billing_requested_by")}>
                    {report.requestedBy?.name ?? t("not_available")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("external_billing_requested_at")}>
                    {formatDate(report.requestedAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("external_billing_confirmed_by")}>
                    {report.billingConfirmedBy?.name ?? t("not_available")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("external_billing_confirmed_at")}>
                    {formatDate(report.billingConfirmedAt)}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("external_billing_download_count")}
                  >
                    {report.downloadCount ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("external_billing_last_downloaded_at")}
                  >
                    {formatDate(report.lastDownloadedAt)}
                  </Descriptions.Item>
                </Descriptions>

                <div className={styles.actions}>
                  {report.canDownload === true ? (
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      loading={action === "download"}
                      disabled={action !== null && action !== "download"}
                      onClick={handleDownload}
                    >
                      {t("external_billing_download")}
                    </Button>
                  ) : null}
                  {report.canConfirmBilling === true ? (
                    <Button
                      danger
                      icon={<CheckCircleOutlined />}
                      loading={action === "confirm"}
                      disabled={action !== null && action !== "confirm"}
                      onClick={handleConfirm}
                    >
                      {t("external_billing_confirm_action")}
                    </Button>
                  ) : null}
                  {report.canRetry === true ? (
                    <Button
                      icon={<ReloadOutlined />}
                      loading={action === "retry"}
                      disabled={action !== null && action !== "retry"}
                      onClick={handleRetry}
                    >
                      {t("external_billing_retry")}
                    </Button>
                  ) : null}
                </div>
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ExternalBillingReportPage;
